import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import pic1 from "../../../assets/images/graphic design.jpg";

const GraphicDesigning = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="bg-gray-100 text-gray-800" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  {" "}
                  🎨 Bring Your Brand to Life with Right{" "}
                </span>
                <span className="text-blue-600"> On Time Services</span>
              </h1>
              <p style={{ fontWeight: "bold" }}>
                Creative Graphic Design That Captivates, Communicates & Converts
              </p>
              <p className="text-white mb-8 mt-4">
                In today's digital-first world, design is more than
                decoration—it's how your brand speaks. At Right On Time
                Services, we blend creativity with strategy to deliver graphic
                design solutions that spark engagement and make your brand
                unforgettable.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      {/* About Section */}
      <section className="py-20 bg-white px-4 md:px-10" data-aos="fade-up">
        <div className="container mx-auto grid md:grid-cols-2 gap-8 items-center">
          {/* Left Image */}
          <div className="flex justify-center">
            <img
              src={pic1} // 🔁 Replace with your actual image path
              alt="Design Language"
              className="w-full max-w-md h-auto object-contain rounded-xl"
            />
          </div>

          {/* Right Content */}
          <div>
            <h2 className="text-3xl font-bold text-blue-900 mb-6 text-left">
              ✨ Design Is the Language of Your Brand
            </h2>
            <p className="text-lg text-gray-800 leading-relaxed">
              Graphic design is the art of storytelling through visuals. With a
              deep understanding of typography, color theory, layout, and brand
              identity, our designers craft materials that are both beautiful
              and functional— designed to inform, inspire, and drive results.
              From stunning logos to immersive website layouts, our mission is
              to turn your ideas into compelling visuals that resonate with your
              audience.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-100" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-blue-900 text-center">
          🧩 Our Graphic Design Services
        </h2>
        {/* <p className="mt-4 px-8 max-w-4xl mx-auto">We Create Visuals that Leave a Lasting Impression!</p> */}
        <div
          className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-2 gap-8 px-8 mt-6"
          style={{ marginTop: "30px" }}
        >
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">🎯 Logo Design</h3>
            <p className="mt-2">
              Crafting unique and memorable logos that encapsulate your brand’s
              identity.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🧱 Branding & Identity Design
            </h3>
            <p className="mt-2">
              Building cohesive, scalable brand systems—from typography to color
              palettes and beyond.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🌐 Website Design
            </h3>
            <p className="mt-2">
              Designing user-centric, visually immersive websites that turn
              visitors into customers.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              📱 Social Media Graphics
            </h3>
            <p className="mt-2">
              Creating scroll-stopping visuals optimized for platforms like
              Instagram, Facebook, LinkedIn, and more.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🖨️ Print & Marketing Collateral
            </h3>
            <p className="mt-2">
              High-quality designs for brochures, flyers, business cards,
              packaging, and more.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Inbound Call Management Section */}
      <section className="py-16 bg-blue-50 px-6" data-aos="fade-up">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center">
          {/* Left Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:w-1/2 mb-10 lg:mb-0 lg:pr-12"
          >
            <h2 className="text-3xl font-bold text-gray-900">
              🌀 Our Process:
              <span className="text-blue-500">From Vision to Visual</span>
            </h2>
            <p className="text-gray-700 mt-4">
              We believe that great design starts with understanding. Here’s how
              we bring your concepts to life:
            </p>
            <ul className="mt-6 space-y-4" style={{ marginTop: "30px" }}>
              {[
                {
                  title: "Discovery",
                  description:
                    "We dive deep into your brand, goals, and target audience.",
                },
                {
                  title: "Conceptualization",
                  description:
                    "Mood boards, mockups, and sketches based on your input.",
                },
                {
                  title: "Design Execution ",
                  description:
                    "Meticulous design using industry-leading tools and creative precision.",
                },
                {
                  title: "Revisions",
                  description:
                    "Your feedback is integrated until perfection is reached.",
                },
                {
                  title: "Delivery",
                  description:
                    "Final files provided in multiple formats, ready for digital or print.",
                },
              ].map((service, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="flex bg-white p-4 shadow-md rounded-lg"
                  data-aos="fade-up"
                >
                  <div className="w-10 h-10 bg-blue-500 flex items-center justify-center rounded-full mr-4">
                    <span className="text-lg font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-600">{service.title}</h3>
                    <p className="text-gray-600 text-sm">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </ul>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:w-1/2"
          ></motion.div>
        </div>
      </section>

      {/* Achievements Section */}
      <div
        className="py-20 bg-gray-50 opacity-0 transition-opacity duration-1000 mb-16 mb-16"
        data-aos="fade-up"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Portfolio</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Take a look at standout design work we’ve delivered for clients
              like:
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-full p-8 shadow-md flex items-center justify-center transition-transform duration-500 hover:scale-110">
              <div className="text-center">
                <Award className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-gray-600">
                  Extended Health Services
                </div>
              </div>
            </div>
            <div className="bg-white rounded-full p-8 shadow-md flex items-center justify-center transition-transform duration-500 hover:scale-110">
              <div className="text-center">
                <Award className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-gray-600">
                  Scribe Align LLC
                </div>
              </div>
            </div>
            <div className="bg-white rounded-full p-8 shadow-md flex items-center justify-center transition-transform duration-500 hover:scale-110">
              <div className="text-center">
                <Award className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-gray-600">
                  Clinical Heroes
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us? */}
      <section className="py-16 bg-white" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-blue-900 text-center">
          🌟 Why Choose Right On Time Services?
        </h2>
        <ul
          className="list-disc list-inside text-lg max-w-4xl mx-auto"
          style={{ marginTop: "30px" }}
        >
          <li>
            ✔️ Creative Experts: Skilled professionals who bring originality and
            vision to every project.
          </li>
          <li>
            ✔️ Budget-Friendly Excellence: Get top-tier design quality at
            competitive pricing.
          </li>
          <li>
            ✔️ Streamlined Workflow: Efficient processes that respect your time
            and meet your deadlines.
          </li>
          <li>
            ✔️ Client-Centric Revisions: Collaborative approach with unlimited
            adjustments until you’re 100% satisfied.
          </li>
          <li>
            ✔️ Fast Turnaround, Always On Time: Because your deadlines matter
            just as much as your designs.
          </li>
        </ul>
      </section>

      {/* Call to Action */}
      <motion.section
        className="py-16 text-center bg-white shadow-md rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <h2 className="text-3xl font-bold text-blue-900">
          📞 Ready to Transform Your Brand's Visual Identity?
        </h2>
        <p className="mt-4 px-8 max-w-4xl mx-auto">
          👉 Contact Right On Time Services today to bring your brand story to
          life through bold, impactful design.
        </p>
      </motion.section>
    </div>
  );
};

export default GraphicDesigning;
